import { httpGet, httpPost } from "../Web";
import { ApiResponse } from "./ApiResponse";
import { WorkshopProductCombination } from "./WorkshopProductCombination";

export enum WorkshopTestResult {
    NotComplete = 0, Passed = 1, Failed = 2, Warning = 3, ServerNotSupported = 4, ClientNotSupported = 5
};
export const WorkshopTestResultStrings = ["Not Complete", "Passed", "Failed", "Warning", "Server Not Supported", "Client Not Supported"];

export interface WorkshopTestCaseResult {
    id?: number,
    guid: string,
    comment: string,
    value: string | null,
    result?: WorkshopTestResult,
    testCaseId: number,
    workshopResultId?: number,
};

export interface WorkshopResult {
    id?: number,
    guid?: string | null,
    comment: string | null,
    started?: boolean,
    clientAccepted?: boolean,
    serverAccepted?: boolean,
    testResult?: WorkshopTestResult,
    counterCalcId?: number,
    serverNotSupported?: number,
    clientNotSupported?: number,
    testsNotCompleted?: number,
    testsWarning: number,
    testsFailed: number,
    testsPassed: number,
    testCombinationId: number,
    clientId: number,
    serverId: number,
    workshopId: number,
    interfaceId: number
}

export interface WorkshopResultCounters {
    workshopProductId: number,  // The workshop product ID this result belongs to, top level of tree should always be set
    consumerWPId: number,       // If set then top level is provider, lowest level of tree. If 0, then not for this level
    providerWPId: number,       // If set then top level is consumer, lowest level of tree. If 0, then not for this level
    interfaceId: number,        // The interface ID this result belongs to, if any, forth level of tree. If 0, then not for this level
    companyId: number,          // The company ID this result belongs to, if any, fifth level of tree. If 0, then not for this level
    testCombinationId: number,  // The test combination ID this result belongs to, if any, second level of tree. If 0, then is top level
    testsPassed: number,        // Test Resuult sumary for this level of tree
    testsFailed: number,        // Test Resuult sumary for this level of tree
    testsWarning: number,       // Test Resuult sumary for this level of tree
    testsNotCompleted: number,  // Test Resuult sumary for this level of tree
    isConsumer: boolean,        // if true, then isProvider must be false, third level of tree. If both are false, then not for this level
    isProvider: boolean         // if true, then isConsumer must be false, third level of tree. If both are false, then not for this level
}

export const GetEmptyResultCounters = () => {
    let counters: WorkshopResultCounters = {
        workshopProductId: 0,
        consumerWPId: 0,
        providerWPId: 0,
        interfaceId: 0,
        companyId: 0,
        testCombinationId: 0,
        testsPassed: 0,
        testsFailed: 0,
        testsWarning: 0,
        testsNotCompleted: 0,
        isConsumer: false,
        isProvider: false
    }
    return counters;
}

export type WpIdWsResultsMap = Map<number, WorkshopResult[]>;

export const getWorkshopResults = async (workshopProductIds: number[], abortController: AbortController): Promise<WpIdWsResultsMap> => {
    const workshopProductResultMap: Map<number, WorkshopResult[]> = new Map();

    try {
        const requestResults = await Promise.all(workshopProductIds.map(wpId =>  getWorkshopResultsForWP(wpId, abortController)));

        requestResults.forEach((apiResponse, index) => workshopProductResultMap.set(workshopProductIds[index], apiResponse.result))
    } catch(error) {
        return Promise.reject();
    }

    return Promise.resolve(workshopProductResultMap);
}

export const getResultCounters = (workshopId: number, abortController: AbortController): Promise<ApiResponse<WorkshopResultCounters[]>> =>
    httpGet(`/api/result/getresultcounters/${workshopId}`, abortController);

export const getWorkshopResultsForWP = (workshopProductId: number, abortController: AbortController): Promise<ApiResponse<WorkshopResult[]>> =>
    httpGet(`/api/result/${workshopProductId}`, abortController);

export const getWorkshopResultsForWorkshop = (workshopId: number, abortController: AbortController): Promise<ApiResponse<WorkshopResult[]>> =>
    httpGet(`/api/result/workshopselect/${workshopId}`, abortController);

//export const updateWsResult = (wsResult: WorkshopResult, abortController: AbortController): Promise<ApiResponse<WorkshopResult>> =>
//    httpPost(`/api/result/update/`, wsResult, abortController);

export const getWSTestCaseResult = (workshopResultId: number, abortController: AbortController): Promise<ApiResponse<WorkshopTestCaseResult[]>> =>
    httpGet(`/api/result/testcase/${workshopResultId}`, abortController);

export const updateWSTestCaseResult = (tcResult: WorkshopTestCaseResult, abortController: AbortController): Promise<ApiResponse<WorkshopTestCaseResult>> =>
    httpPost(`/api/result/testcase/update/`, tcResult, abortController);

export const updateWsResultConv = (wsResult: WorkshopResult, abortController: AbortController): Promise<ApiResponse<WorkshopResult>> =>
    httpPost(`/api/result/updateresults/`, wsResult, abortController);

export const insertresultheaders = (wptc: WorkshopProductCombination, abortController: AbortController): Promise<ApiResponse<WorkshopProductCombination>> =>
    httpPost(`/api/result/insertresultheaders/`, wptc, abortController);
